import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { LinkButton, Text, Tooltip } from '@etg/wings';
import { css, cx } from '@eti/styles';
import { useProperty, useTranslation } from '@eti/providers';
import { constants, price as priceUtil } from '@eti/utils';
import { checkInBaggage, productIds, secondBaggagePerTraveler } from '@eti/products';
import { ScreenReaderPrice } from '@eti/components';
import { getAgeTypeLabel } from '../../../../../utils/travelers';
import {
  getTravelerProductsTotalPrice,
  sortProductsByAlpabethicOrder,
  getTaxesAndFeesTotal,
  isSpecificProductsSelected,
  sortCarriers,
} from '../utils';
import { CartRow, Price } from '../../CartComponents';
import CartToggleableRow from '../../CartToggleableRow';
import useNameFormatter from '../../../../../common/custom-hooks/useNameFormatter';
import CartTaxesAndFees from './CartTaxesAndFees';
import CartTaxesAndFeesCarrierPriceBreakdown from './CartTaxesAndFeesCarrierPriceBreakdown';
import CartPerAgeTypeCarrierPriceBreakdown from './CartPerAgeTypeCarrierPriceBreakdown';
import CartProductCarrierPriceBreakdown from './CartProductCarrierPriceBreakdown';

const travelerWrapperStyles = css`
  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const cartRowWrapperStyles = css`
  align-content: flex-end;
  display: flex;
  flex-wrap: wrap;
`;

const CartTravelerProducts = ({
  travelers,
  marketingCarrierNames,
  includedExtraProducts,
  isRyanairInTrip,
  uniqueCarriers = [],
}) => {
  const isPaymentPage = window.location.pathname.includes('/payment');
  const { t } = useTranslation();
  const { p } = useProperty();
  const taxesAndFeeLabel = t('Cart.TaxesAndFees.Label');
  const excludedProductsFromMarkup = p('IbeClient.PriceBreakdown.ProductsIdsToExcludeMarkup') || [];

  const ageTypeLabel = (ageType) => t(getAgeTypeLabel(ageType)).toLowerCase();
  const commaSeparator = p('IbeClient.CommaSeparator.Enabled') ? ',' : '';

  const { formatName } = useNameFormatter();

  const getProductPriceBreakdownData = (price, productMarkup, priceSecondBag, secondBagMarkup) => {
    return productMarkup || secondBagMarkup
      ? [
          {
            label: t('SeatMap.PriceInformation.Markup'),
            value:
              secondBagMarkup && secondBagMarkup !== productMarkup
                ? productMarkup || 0 + secondBagMarkup
                : productMarkup,
          },
          {
            label: t('SeatMap.PriceInformation.ProviderCost'),
            value:
              secondBagMarkup && secondBagMarkup !== productMarkup
                ? priceSecondBag + price - (productMarkup || 0 + secondBagMarkup)
                : price - productMarkup,
          },
        ]
      : null;
  };

  const showTDToggleableCart = (
    ancillaryCarrierPriceBreakdowns,
    index,
    title,
    priceRaw,
    id,
    productMarkup,
    isSpecificProductExcludedFromMarkup,
    pricePerAgeTypeLabel,
  ) => {
    const productName = productIds[Number(id)];
    const showCarrierPriceBreakdown =
      isRyanairInTrip &&
      isSpecificProductExcludedFromMarkup &&
      ancillaryCarrierPriceBreakdowns.length > 0;

    if (productMarkup > 0) {
      if (showCarrierPriceBreakdown) {
        return (
          <CartProductCarrierPriceBreakdown
            ancillaryCarrierPriceBreakdowns={sortCarriers(
              ancillaryCarrierPriceBreakdowns,
              uniqueCarriers,
            )}
            key={index}
            pricePerAgeTypeLabel={pricePerAgeTypeLabel}
            productDisplayName={title}
            productName={productName}
            totalProductPrice={priceRaw - productMarkup}
            travelerIndex={index}
          />
        );
      }

      return (
        <CartToggleableRow
          data={getProductPriceBreakdownData(priceRaw, productMarkup)}
          dataTestId={`cart-product-${productIds[Number(id)]}`}
          key={id}
          price={priceRaw}
          title={title}
        />
      );
    }

    return (
      priceRaw > 0 && (
        <Fragment key={`traveler-${index}-product-${id}`}>
          {showCarrierPriceBreakdown ? (
            <CartProductCarrierPriceBreakdown
              ancillaryCarrierPriceBreakdowns={sortCarriers(
                ancillaryCarrierPriceBreakdowns,
                uniqueCarriers,
              )}
              pricePerAgeTypeLabel={pricePerAgeTypeLabel}
              productDisplayName={title}
              productName={productName}
              totalProductPrice={priceRaw - productMarkup}
              travelerIndex={index}
            />
          ) : (
            <CartRow data-testid={`cart-product-${productName}`} key={id}>
              <Text>{title}</Text>
              <Price aria-hidden data-testid={`cart-product-${productName}-price`}>
                {priceUtil.formatPrice(priceRaw)}
              </Price>
              <ScreenReaderPrice price={priceUtil.formatPrice(priceRaw)} />
            </CartRow>
          )}
        </Fragment>
      )
    );
  };

  const showPaymentToggleableCart = (
    ancillaryCarrierPriceBreakdowns,
    index,
    title,
    priceRaw,
    id,
    productMarkup,
    secongBagDetails,
    isSpecificProductExcludedFromMarkup,
    pricePerAgeTypeLabel,
  ) => {
    const productName = productIds[Number(id)];
    const isCheckedBagIncluded = includedExtraProducts?.some(
      (product) => product.id === String(checkInBaggage.id),
    );

    const titleWhenCheckedBagIncluded = isCheckedBagIncluded
      ? includedExtraProducts?.map((product) => product.texts?.name).toString()
      : null;

    const productDisplayName =
      isCheckedBagIncluded && id === String(secondBaggagePerTraveler.id)
        ? titleWhenCheckedBagIncluded
        : title;

    const { priceRaw: priceSecondBag, markup: secondBagMarkup } = secongBagDetails || {};
    if (!isCheckedBagIncluded && id === String(secondBaggagePerTraveler.id)) {
      return null;
    }

    const showCarrierPriceBreakdown =
      isRyanairInTrip &&
      isSpecificProductExcludedFromMarkup &&
      ancillaryCarrierPriceBreakdowns.length > 0;

    if (productMarkup > 0 || secondBagMarkup > 0) {
      if (showCarrierPriceBreakdown) {
        return (
          <CartProductCarrierPriceBreakdown
            ancillaryCarrierPriceBreakdowns={sortCarriers(
              ancillaryCarrierPriceBreakdowns,
              uniqueCarriers,
            )}
            pricePerAgeTypeLabel={pricePerAgeTypeLabel}
            productDisplayName={title}
            productName={productName}
            totalProductPrice={priceRaw - productMarkup}
            travelerIndex={index}
          />
        );
      }

      return (
        <CartToggleableRow
          data={
            id === String(checkInBaggage.id) || id === String(secondBaggagePerTraveler.id)
              ? getProductPriceBreakdownData(
                  priceRaw,
                  productMarkup,
                  priceSecondBag,
                  secondBagMarkup,
                )
              : getProductPriceBreakdownData(priceRaw, productMarkup)
          }
          dataTestId={`cart-product-${productName}`}
          id={id}
          key={id}
          price={
            id === String(checkInBaggage.id) && priceSecondBag
              ? priceRaw + priceSecondBag
              : priceRaw
          }
          title={productDisplayName}
        />
      );
    }

    const totalProductPrice =
      id === checkInBaggage.id.toString() ? priceRaw + (priceSecondBag || 0) : priceRaw;

    return (
      priceRaw > 0 && (
        <Fragment key={`traveler-${index}-product-${id}`}>
          {showCarrierPriceBreakdown ? (
            <CartProductCarrierPriceBreakdown
              ancillaryCarrierPriceBreakdowns={sortCarriers(
                ancillaryCarrierPriceBreakdowns,
                uniqueCarriers,
              )}
              pricePerAgeTypeLabel={pricePerAgeTypeLabel}
              productDisplayName={title}
              productName={productName}
              totalProductPrice={totalProductPrice}
              travelerIndex={index}
            />
          ) : (
            <CartRow data-testid={`cart-product-${productName}`} key={id}>
              <Text>{productDisplayName}</Text>
              <Price aria-hidden data-testid={`cart-product-${productName}-price`}>
                {priceUtil.formatPrice(totalProductPrice)}
              </Price>
              <ScreenReaderPrice price={priceUtil.formatPrice(totalProductPrice)} />
            </CartRow>
          )}
        </Fragment>
      )
    );
  };

  return (
    <div data-testid="cart-travelers">
      {travelers.length > 0 &&
        travelers.map(
          (
            {
              ageType,
              firstName,
              fullName,
              lastName,
              markup,
              vat,
              taxesAndFees = [],
              carrierPriceBreakdowns = [],
              products,
              totalPrice,
              travelerId,
            },
            index,
          ) => {
            const sortedCarrierPriceBreakdowns = sortCarriers(
              carrierPriceBreakdowns,
              uniqueCarriers,
            );

            const pricePerAgeTypeLabel = t('Cart.PricePerAgeType', ageTypeLabel(ageType));
            const secongBagDetails = sortProductsByAlpabethicOrder(products).find(
              ({ id }) => id === String(secondBaggagePerTraveler.id),
            );

            const travelerTotalPrice = isRyanairInTrip
              ? getTravelerProductsTotalPrice(
                  totalPrice - (markup ?? 0),
                  products,
                  excludedProductsFromMarkup,
                )
              : getTravelerProductsTotalPrice(totalPrice, products);

            const taxesAndFeesTotal = getTaxesAndFeesTotal(taxesAndFees, vat, markup);
            const pricePerAgeTypeTotalPrice = totalPrice - taxesAndFeesTotal;

            return (
              <div
                className={travelerWrapperStyles}
                data-testid={`traveler-${index}-products`}
                key={travelerId}
              >
                <CartRow>
                  <div className={cx(cartRowWrapperStyles, constants.MOUSE_FLOW_CLASS_NAMES)}>
                    {fullName ? (
                      <Text as="p" variant="16-bold">
                        {`${formatName(
                          firstName,
                          lastName,
                          fullName,
                        )}${commaSeparator} ${ageTypeLabel(ageType)}`}
                      </Text>
                    ) : (
                      <Text as="p" variant="16-bold">{`${t(
                        'TravelerDescription.Header',
                        index + 1,
                      )}${commaSeparator} ${ageTypeLabel(ageType)}`}</Text>
                    )}
                  </div>
                  <div className={cartRowWrapperStyles}>
                    <Text
                      aria-hidden
                      as="p"
                      data-testid={`traveler-${index}-totalPrice`}
                      variant="16-bold"
                    >
                      {priceUtil.formatPrice(travelerTotalPrice)}
                    </Text>
                    <ScreenReaderPrice price={priceUtil.formatPrice(travelerTotalPrice)} />
                  </div>
                </CartRow>

                {isRyanairInTrip && carrierPriceBreakdowns.length ? (
                  <CartPerAgeTypeCarrierPriceBreakdown
                    carrierPriceBreakdowns={sortedCarrierPriceBreakdowns}
                    pricePerAgeTypeLabel={pricePerAgeTypeLabel}
                    pricePerAgeTypeTotalPrice={pricePerAgeTypeTotalPrice}
                    travelerIndex={index}
                  />
                ) : (
                  <CartRow>
                    <Text>{pricePerAgeTypeLabel}</Text>
                    <Price aria-hidden>{priceUtil.formatPrice(pricePerAgeTypeTotalPrice)}</Price>
                    <ScreenReaderPrice price={priceUtil.formatPrice(pricePerAgeTypeTotalPrice)} />
                  </CartRow>
                )}

                {products.length > 0 &&
                  sortProductsByAlpabethicOrder(products).map(
                    ({
                      ancillaryCarrierPriceBreakdowns = [],
                      title,
                      priceRaw,
                      id,
                      markup: productMarkup,
                    }) => {
                      const isSpecificProductExcludedFromMarkup = isSpecificProductsSelected(
                        String(id),
                        excludedProductsFromMarkup,
                      );

                      return isPaymentPage
                        ? showPaymentToggleableCart(
                            ancillaryCarrierPriceBreakdowns,
                            index,
                            title,
                            priceRaw,
                            id,
                            productMarkup,
                            secongBagDetails,
                            isSpecificProductExcludedFromMarkup,
                            pricePerAgeTypeLabel,
                          )
                        : showTDToggleableCart(
                            ancillaryCarrierPriceBreakdowns,
                            index,
                            title,
                            priceRaw,
                            id,
                            productMarkup,
                            isSpecificProductExcludedFromMarkup,
                            pricePerAgeTypeLabel,
                          );
                    },
                  )}
                {isRyanairInTrip && carrierPriceBreakdowns.length ? (
                  <CartTaxesAndFeesCarrierPriceBreakdown
                    ageType={ageType}
                    carrierPriceBreakdowns={sortedCarrierPriceBreakdowns}
                    pricePerAgeTypeLabel={pricePerAgeTypeLabel}
                    taxesAndFees={taxesAndFees}
                    taxesAndFeesLabel={taxesAndFeeLabel}
                    totalVat={vat}
                    travelerIndex={index}
                  />
                ) : (
                  <CartRow>
                    <Tooltip
                      content={
                        <CartTaxesAndFees
                          ageType={ageType}
                          dataTestId={`traveler-${index}-taxesAndFees`}
                          marketingCarrierNames={marketingCarrierNames}
                          markup={markup}
                          taxesAndFees={taxesAndFees}
                          totalTaxesAndFees={taxesAndFeesTotal}
                          vat={vat}
                        />
                      }
                      data-testid={`cart-taxesAndFees-overlay-traveler-${index}`}
                      title={taxesAndFeeLabel}
                      trigger="click"
                    >
                      <LinkButton data-testid={`traveler-${index}-taxesAndFees-button`}>
                        {taxesAndFeeLabel}
                      </LinkButton>
                    </Tooltip>
                    <Price aria-hidden data-testid={`cart-travelers-${ageType}-vat`}>
                      {priceUtil.formatPrice(taxesAndFeesTotal)}
                    </Price>
                    <ScreenReaderPrice price={priceUtil.formatPrice(taxesAndFeesTotal)} />
                  </CartRow>
                )}
              </div>
            );
          },
        )}
    </div>
  );
};

CartTravelerProducts.propTypes = {
  includedExtraProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      texts: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  ),
  isRyanairInTrip: PropTypes.bool,
  marketingCarrierNames: PropTypes.string,
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      carrierPriceBreakdowns: PropTypes.array,
      firstName: PropTypes.string,
      fullName: PropTypes.string,
      lastName: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      products: PropTypes.array,
      quantity: PropTypes.number,
      taxesAndFees: PropTypes.array,
      totalPrice: PropTypes.number,
      travelerId: PropTypes.string,
      vat: PropTypes.number,
    }),
  ),
  uniqueCarriers: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
};

export default CartTravelerProducts;
